import { useCallback, useState } from 'react'
import { useDismiss } from './dismiss'

/**
 * A `?` beside a word the screen has invented.
 *
 * These tables carry a small vocabulary of their own — `overridden`, `not
 * ranked`, `inherited`, `pack` — each of which is a compressed sentence. The
 * compression is right: the word appears in a cell the width of a threshold and
 * repeats down the column. What was wrong is that the sentence existed only in
 * the head of whoever wrote the table.
 *
 * A `title` attribute alone will not do. It is invisible until hovered, absent
 * on a touch screen, and unreachable from a keyboard — so the explanation is
 * there for the reader who already knows where to look and missing for everyone
 * else. This is a real control: focusable, dismissible with Escape or a click
 * away, and marked as the question it answers.
 */
export function Explain({ term, children }: { term: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  const close = useCallback(() => setOpen(false), [])
  const box = useDismiss<HTMLSpanElement>(open, close)

  return (
    <span className="explain" ref={box}>
      <button
        type="button"
        className="explain-mark"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`What does “${term}” mean?`}
        onClick={() => setOpen((was) => !was)}
      >
        <sup aria-hidden="true">?</sup>
      </button>
      {open && (
        <span className="explain-body" role="note" data-testid="explanation">
          <strong>{term}</strong>
          {children}
        </span>
      )}
    </span>
  )
}
